import React, { useEffect, useState } from "react";
import { Alert, Input, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { usePostExternalMutation } from "../../../redux/externals/externalsAPI";
import GETJwtToken from "../../../redux/authentication/authentication";
import type { ExternalMutationResult } from "../../../interfaces/External.interface";
import ApiClientRoleSelect from "./ApiClientRoleSelect";
import type { ApiClientRoleOption } from "./apiClientRoles";
import apiClientErrorMessage from "./apiClientErrors";

interface ApiClientCreateModalProps {
  open: boolean;
  orgId: string;
  roles: ApiClientRoleOption[];
  rolesLoading: boolean;
  onClose: () => void;
  /** Called with the create response, which carries the one-time secret. */
  onCreated: (created: ExternalMutationResult) => void;
}

/** Creates an API client in orgId, optionally with a role. */
function ApiClientCreateModal({
  open,
  orgId,
  roles,
  rolesLoading,
  onClose,
  onCreated,
}: ApiClientCreateModalProps) {
  const [t] = useTranslation("global");
  const [name, setName] = useState("");
  const [roleId, setRoleId] = useState("");
  const [error, setError] = useState("");
  const [postExternal, { isLoading }] = usePostExternalMutation();

  useEffect(() => {
    if (open) {
      setName("");
      setRoleId("");
      setError("");
    }
  }, [open]);

  const submit = async () => {
    const trimmed = name.trim();
    if (trimmed === "") {
      setError(t("apiClients.nameRequired"));
      return;
    }
    setError("");
    try {
      const token = await GETJwtToken();
      const created = await postExternal({
        token,
        name: trimmed,
        orgId,
        roleId: roleId || undefined,
      }).unwrap();
      onCreated(created);
    } catch (err) {
      setError(apiClientErrorMessage(err, t("apiClients.genericError")));
    }
  };

  return (
    <Modal
      open={open}
      title={t("apiClients.create")}
      okText={t("apiClients.save")}
      cancelText={t("apiClients.cancel")}
      onOk={submit}
      onCancel={onClose}
      confirmLoading={isLoading}
      okButtonProps={{ "data-testid": "api-client-create-submit" } as Record<string, string>}
      cancelButtonProps={{ "data-testid": "api-client-create-cancel" } as Record<string, string>}
      destroyOnClose
    >
      <div data-testid="api-client-create-modal">
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            className="mb-3"
            data-testid="api-client-create-error"
          />
        )}
        <span className="drawer__text">{t("apiClients.name")}</span>
        <Input
          data-testid="api-client-create-name"
          value={name}
          maxLength={255}
          placeholder={t("apiClients.namePlaceholder")}
          onChange={(event) => setName(event.target.value)}
          onPressEnter={submit}
          style={{ marginTop: 6 }}
        />
        <div className="mt-3">
          <span className="drawer__text">{t("apiClients.role")}</span>
          <div style={{ marginTop: 6 }}>
            <ApiClientRoleSelect
              testId="api-client-create-role"
              roles={roles}
              value={roleId}
              onChange={setRoleId}
              loading={rolesLoading}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default ApiClientCreateModal;
